import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Paper, Typography, Button, Avatar, Stack, CircularProgress, Checkbox, Link,
} from '@mui/material';

import { api_call, empty, getToken, setToken } from './helpers/untils';
import { APP_NAME } from './config/constant';
import { sessionActions } from './store';

const getQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    response_type: params.get('response_type'),
    client_id: params.get('client_id'),
    redirect_uri: params.get('redirect_uri'),
    scope: params.get('scope') || '',
    state: params.get('state') || '',
    code_challenge: params.get('code_challenge') || '',
    code_challenge_method: params.get('code_challenge_method') || 'S256',
  };
};

const deviceSubtitle = (device) => {
  const parts = [device.make, device.model].filter((part) => !empty(part));
  return parts.join(' ');
};

const OAuthGrantPage = () => {
  const dispatch = useDispatch();
  const isLoggedIn = !!getToken();

  const [queryParams] = useState(getQueryParams());
  const [client, setClient] = useState(null);
  const [devices, setDevices] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (queryParams.response_type !== 'code' || empty(queryParams.client_id) || empty(queryParams.redirect_uri)) {
      setError('This authorization request is missing required parameters.');
      return;
    }

    if (!isLoggedIn) {
      const redirect_url = window.location.href;
      window.location.href = `/login?redirect_url=${encodeURIComponent(redirect_url)}`;
      return;
    }

    const loadData = async () => {
      const search = new URLSearchParams({
        response_type: queryParams.response_type,
        client_id: queryParams.client_id,
        redirect_uri: queryParams.redirect_uri,
      }).toString();

      const [clientResponse, devicesResponse] = await Promise.all([
        api_call(`/oauth/authorize?${search}`),
        api_call('/api/devices'),
      ]);

      if (clientResponse.ok) {
        const data = await clientResponse.json();
        setClient(data.client);
      } else {
        setError(clientResponse?.error === 'invalid_redirect_uri'
          ? 'This application is not configured for that redirect URL.'
          : 'This application is not registered, or the request is invalid.');
        return;
      }

      if (devicesResponse.ok) {
        const deviceList = await devicesResponse.json();
        const list = Array.isArray(deviceList) ? deviceList : [];
        list.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setDevices(list);
        setSelectedDeviceIds(list.map((device) => device.id));
      } else {
        setDevices([]);
      }
    };
    loadData();
  }, [isLoggedIn]);

  const allSelected = !empty(devices) && selectedDeviceIds.length === devices.length;

  const onToggleSelectAll = () => {
    setSelectedDeviceIds(allSelected ? [] : devices.map((device) => device.id));
  };

  const onToggleDevice = (deviceId) => {
    setSelectedDeviceIds((current) => (current.includes(deviceId)
      ? current.filter((id) => id !== deviceId)
      : [...current, deviceId]));
  };

  const onClickAuthorize = async () => {
    setSubmitting(true);
    const response = await api_call('/oauth/authorize/decision', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...queryParams, approve: true, device_ids: selectedDeviceIds }),
    });
    if (response.ok) {
      const data = await response.json();
      window.location.href = data.redirect_url;
    } else {
      setSubmitting(false);
      setError(response?.error || 'Something went wrong. Please try again.');
    }
  };

  const onClickLoginDifferent = () => {
    setToken('');
    window.localStorage.removeItem('session');
    dispatch(sessionActions.updateUser({ id: 0 }));
    const redirect_url = window.location.href;
    window.location.href = `/login?redirect_url=${encodeURIComponent(redirect_url)}`;
  };

  const loading = !client || !devices;

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', backgroundColor: '#f4f4f4', p: 2, paddingTop: { xs: 4, sm: 8 },
    }}
    >
      <Paper elevation={3} sx={{ maxWidth: 460, width: '100%', p: 4 }}>
        {error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h5" align="center" sx={{ fontWeight: 700, mb: 3 }}>
              Authorize Data Access
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Avatar variant="rounded" src={client.icon_url || client.logo_url || undefined} sx={{ width: 72, height: 72 }}>
                {(client.name || '?').charAt(0).toUpperCase()}
              </Avatar>
            </Box>

            <Typography align="center" sx={{ mb: 3 }}>
              Select the vehicles to grant data access for <b>{client.name}</b>
            </Typography>

            {empty(devices) ? (
              <Typography color="textSecondary" align="center" sx={{ mb: 3 }}>
                No vehicles found on your account.
              </Typography>
            ) : (
              <>
                <Link component="button" type="button" underline="hover" onClick={onToggleSelectAll} sx={{ mb: 1, display: 'inline-block' }}>
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Link>
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f7f8fb', mb: 3 }}>
                  {devices.map((device, index) => (
                    <Stack
                      key={device.id}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      onClick={() => onToggleDevice(device.id)}
                      sx={{
                        px: 1.5,
                        py: 1,
                        cursor: 'pointer',
                        borderTop: index === 0 ? 'none' : '1px solid #e0e0e0',
                      }}
                    >
                      <Checkbox
                        checked={selectedDeviceIds.includes(device.id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => onToggleDevice(device.id)}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{device.name || `Device ${device.id}`}</Typography>
                        {deviceSubtitle(device) && (
                          <Typography variant="caption" color="textSecondary">{deviceSubtitle(device)}</Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Box>
              </>
            )}

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={submitting || empty(selectedDeviceIds)}
              onClick={onClickAuthorize}
              sx={{ mb: 3 }}
            >
              Authorize Access
            </Button>

            <Typography align="center">
              <Link component="button" type="button" underline="hover" onClick={onClickLoginDifferent}>
                {`Login to a different ${APP_NAME || 'MooveTrax'} account`}
              </Link>
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default OAuthGrantPage;
